/**
 * CinematicOverlay — letterbox bars, title card, fade transitions, end card.
 */

import { useEffect, useState } from "react";

interface Props {
  missionName: string;
  showTitleCard: boolean;
  fadeState: "in" | "out" | "none";
  missionEnded: boolean;
  endSummary: string | null;
}

export function CinematicOverlay({
  missionName,
  showTitleCard,
  fadeState,
  missionEnded,
  endSummary,
}: Props) {
  const [titleOpacity, setTitleOpacity] = useState(0);
  const [endCardOpacity, setEndCardOpacity] = useState(0);

  // Title card fade in/out
  useEffect(() => {
    if (showTitleCard) {
      // Fade in after brief delay
      const fadeIn = setTimeout(() => setTitleOpacity(1), 500);
      // Fade out before title card ends
      const fadeOut = setTimeout(() => setTitleOpacity(0), 3000);
      return () => { clearTimeout(fadeIn); clearTimeout(fadeOut); };
    } else {
      setTitleOpacity(0);
    }
  }, [showTitleCard]);

  // End card fade in
  useEffect(() => {
    if (missionEnded) {
      const timer = setTimeout(() => setEndCardOpacity(1), 1000);
      return () => clearTimeout(timer);
    } else {
      setEndCardOpacity(0);
    }
  }, [missionEnded]);

  const stardate = `${41000 + Math.floor(Math.random() * 999)}.${Math.floor(Math.random() * 10)}`;

  return (
    <div className="cinematic-overlay" style={{ pointerEvents: "none" }}>
      {/* Letterbox bars */}
      <div className="letterbox-top" />
      <div className="letterbox-bottom" />

      {/* Title card */}
      <div
        className="title-card"
        style={{ opacity: titleOpacity }}
      >
        <div className="title-card-mission">{missionName}</div>
        <div className="title-card-stardate">STARDATE {stardate}</div>
      </div>

      {/* Scene transition fade */}
      {fadeState !== "none" && (
        <div
          className="scene-fade"
          style={{
            opacity: fadeState === "in" ? 1 : 0,
            transition: fadeState === "in" ? "opacity 0.4s ease-in" : "opacity 0.8s ease-out",
          }}
        />
      )}

      {/* End card */}
      {missionEnded && (
        <div className="end-card" style={{ opacity: endCardOpacity }}>
          <div className="end-card-label">MISSION COMPLETE</div>
          {endSummary && <div className="end-card-summary">{endSummary}</div>}
          <div className="end-card-stardate">STARDATE {stardate}</div>
        </div>
      )}
    </div>
  );
}
